import React, { useState, useEffect, useCallback } from 'react';

const BACKEND = 'https://leaderboard-backend-qpmb.onrender.com';
const REFRESH_INTERVAL = 30000;

const TABS = [
  { key: 'daisy',   label: 'Daisies',                cls: 'tab-daisy',   match: g => /dais/i.test(g) },
  { key: 'brownie', label: 'Brownies',               cls: 'tab-brownie', match: g => /brownie/i.test(g) },
  { key: 'junior',  label: 'Juniors / Cadettes',     cls: 'tab-junior',  match: g => /junior|cadet/i.test(g) },
  { key: 'senior',  label: 'Seniors / Ambassadors',  cls: 'tab-senior',  match: g => /senior|ambassador/i.test(g) },
];

function fmt(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  const msp = Math.floor(ms % 1000);
  return m + ':' + String(sec).padStart(2, '0') + '.' + String(msp).padStart(3, '0');
}

export default function Leaderboard() {
  const [allData, setAllData] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [pinned, setPinned] = useState(null);
  const [lastUpd, setLastUpd] = useState('Connecting to leaderboard…');
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showWinners, setShowWinners] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gsPin'));
      if (saved) setPinned(saved);
    } catch (e) {}
  }, []);

  // Fetch leaderboard data
  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(BACKEND + '/api/leaderboard');
      if (!r.ok) throw new Error();
      const data = await r.json();
      setAllData(data);
      const now = new Date();
      setLastUpd('Last updated ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch (e) {
      setLastUpd('Reconnecting…');
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [fetchData]);

  // Fetch photos from backend
  const fetchPhotos = useCallback(async () => {
    try {
      const r = await fetch(BACKEND + '/api/photos/list');
      if (r.ok) {
        const data = await r.json();
        setPhotos(data || []);
      }
    } catch (e) {
      console.error('Failed to fetch photos:', e);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
    const id = setInterval(fetchPhotos, 10000);
    return () => clearInterval(id);
  }, [fetchPhotos]);

  const pinEntry = (entry) => {
    setPinned(entry);
    try { localStorage.setItem('gsPin', JSON.stringify(entry)); } catch (e) {}
  };

  const pinFromSearch = (entry) => {
    pinEntry(entry);
    setSearch('');
    setSearchOpen(false);
  };

  const unpin = () => {
    setPinned(null);
    try { localStorage.removeItem('gsPin'); } catch (e) {}
  };

  // Check if it's past July 25, 2026 7 PM EST
  const isConferenceEnded = () => {
    const now = new Date();
    const endTime = new Date('2026-07-25T19:00:00-04:00'); // 7 PM EDT (UTC-4 in July)
    return now >= endTime;
  };

  // Compute winners (top 3 per age group)
  const getWinners = () => {
    const winners = {};
    TABS.forEach(tab => {
      const grouped = allData
        .filter(e => tab.match(e.group || ''))
        .sort((a, b) => a.elapsedTimeInMilliseconds - b.elapsedTimeInMilliseconds)
        .slice(0, 3);
      winners[tab.key] = grouped.map((e, i) => ({ ...e, place: i + 1 }));
    });
    return winners;
  };

  const tab = TABS[currentTab];

  const indiv = allData
    .filter(e => tab.match(e.group || ''))
    .sort((a, b) => a.elapsedTimeInMilliseconds - b.elapsedTimeInMilliseconds)
    .map((e, i) => ({ ...e, groupRank: i + 1 }));

  const q = search.trim().toLowerCase();
  const searchResults = q
    ? (() => {
        const rankedByGroup = {};
        TABS.forEach(t => {
          rankedByGroup[t.key] = allData
            .filter(e => t.match(e.group || ''))
            .sort((a, b) => a.elapsedTimeInMilliseconds - b.elapsedTimeInMilliseconds)
            .map((e, i) => ({ ...e, groupRank: i + 1 }));
        });
        const all = Object.values(rankedByGroup).flat();
        return all
          .filter(e => {
            const name = (e.firstName + ' ' + e.lastInitial).toLowerCase();
            return name.includes(q) || (e.troopNumber || '').toString().includes(q);
          })
          .sort((a, b) => a.elapsedTimeInMilliseconds - b.elapsedTimeInMilliseconds)
          .slice(0, 8);
      })()
    : [];

  let livePinned = null;
  if (pinned) {
    const pinnedGroupTab = TABS.find(t => t.match(pinned.group || ''));
    if (pinnedGroupTab) {
      const ranked = allData
        .filter(e => pinnedGroupTab.match(e.group || ''))
        .sort((a, b) => a.elapsedTimeInMilliseconds - b.elapsedTimeInMilliseconds)
        .map((e, i) => ({ ...e, groupRank: i + 1 }));
      livePinned = ranked.find(e =>
        e.firstName === pinned.firstName &&
        e.lastInitial === pinned.lastInitial &&
        e.troopNumber === pinned.troopNumber
      ) || pinned;
    } else {
      livePinned = pinned;
    }
  }

  const rankBubble = (r) => {
    const cls = r === 1 ? 'r1' : r === 2 ? 'r2' : r === 3 ? 'r3' : 'rn';
    const lbl = r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : r;
    return <div className={'rank-bubble ' + cls}>{lbl}</div>;
  };

  const pinnedGroupName = livePinned
    ? (TABS.find(t => t.match(livePinned.group || ''))?.label.replace(/^[^\s]+\s/, '') || '')
    : '';

  return (
    <div className="app">
      <style>{CSS}</style>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="modal-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPhoto(null)}>✕</button>
            <img src={selectedPhoto.cloudinary_url} alt="" className="photo-modal-img" />
          </div>
        </div>
      )}

      {/* Winners Announcement Modal */}
      {showWinners && (() => {
        const winners = getWinners();
        return (
          <div className="modal-overlay winners-overlay" onClick={() => setShowWinners(false)}>
            <div className="winners-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowWinners(false)}>✕</button>
              <div className="winners-title">🏆 Conference Champions 🏆</div>
              <div className="winners-grid">
                {TABS.map(tab => (
                  <div key={tab.key} className="winners-group">
                    <div className="winners-group-title">{tab.label}</div>
                    {winners[tab.key].length > 0 ? (
                      winners[tab.key].map((winner, idx) => (
                        <div key={idx} className="winner-row">
                          <span className="winner-medal">{winner.place === 1 ? '🥇' : winner.place === 2 ? '🥈' : '🥉'}</span>
                          <span className="winner-name">{winner.firstName} {winner.lastInitial}</span>
                          <span className="winner-time">{fmt(winner.elapsedTimeInMilliseconds)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-winners">No data yet</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Winners Toggle Button - Only show after July 25, 7 PM EST */}
      {isConferenceEnded() && (
        <button className="winners-toggle" onClick={() => setShowWinners(true)} title="Show winners">
          🏆
        </button>
      )}

      {/* 1. Header */}
      <div className="header">
        <div className="header-inner">
          <img src="/EDS Logo White.png" alt="Evans Distribution Systems" className="header-logo" />
          <div className="header-titles">
            <div className="header-main">Evans Fulfillment Challenge ⚡</div>
            <div className="header-sub">Girl Scouts Unite Leaderboard</div>
          </div>
        </div>
      </div>

      {/* 2. Evans Blurb Banner */}
      <div className="evans-banner">
        <p className="evans-text">
          Evans Distribution Systems is a privately owned third-party logistics (3PL) provider based in Melvindale, Michigan. See how Evans supports Girl Scouts through its nationwide fulfillment and Digital Cookie program.{' '}
          <a href="https://www.evansdist.com/girlscouts" target="_blank" rel="noopener noreferrer" className="evans-link">
            Learn more →
          </a>
        </p>
      </div>

      {/* 3. Live Ribbon */}
      <div className="gs-ribbon">
        <div className="live-pill"><span className="live-dot"></span>🟢 LIVE</div>
        <div className="refresh-text">Refreshes every 30s</div>
      </div>

      {/* 4. Search */}
      <div className="search-wrap">
        <div className="search-inner">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Find your name or troop number…"
            value={search}
            onChange={e => { setSearch(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
          />
          {search && (
            <button className="search-clear" onClick={() => { setSearch(''); setSearchOpen(false); }} title="Clear">✕</button>
          )}
        </div>
        {searchOpen && q && (
          <div className="search-dropdown">
            {searchResults.length ? (
              searchResults.map((e, idx) => {
                const gName = TABS.find(t => t.match(e.group || ''))?.label || '';
                return (
                  <div key={idx} className="search-result" onClick={() => pinFromSearch(e)}>
                    <div className="sr-rank">#{e.groupRank}</div>
                    <div className="sr-info">
                      <div className="sr-name">{e.firstName} {e.lastInitial}</div>
                      <div className="sr-meta">Troop {e.troopNumber} · {gName}</div>
                    </div>
                    <div className="sr-time">{fmt(e.elapsedTimeInMilliseconds)}</div>
                    <span className="sr-pin">📌</span>
                  </div>
                );
              })
            ) : (
              <div className="search-empty">No one found matching "{search}"</div>
            )}
          </div>
        )}
      </div>

      {/* 5. Pinned Card */}
      <div className="pinned-section">
        {!livePinned ? (
          <div className="no-pinned">
            <div className="no-pinned-icon">📌</div>
            <div className="no-pinned-text">Pin your score right here!</div>
            <div className="no-pinned-hint">Search your name, then tap your row to pin it</div>
          </div>
        ) : (
          <div className="pinned-card">
            <button className="unpin-btn" onClick={unpin} title="Unpin">✕</button>
            <span className="pinned-sparkle1">✨</span>
            <span className="pinned-sparkle2">⭐</span>
            <div className="pinned-label-row">
              <div className="pinned-badge">⭐ MY SCORE</div>
              <div className="pinned-label-text">pinned to your device</div>
            </div>
            <div className="pinned-content">
              <div className="pinned-trophy">🏆</div>
              <div className="pinned-info">
                <div className="pinned-name">
                  {livePinned.groupRank ? '#' + livePinned.groupRank + ' — ' : ''}{livePinned.firstName} {livePinned.lastInitial}
                </div>
                <div className="pinned-troop">
                  Troop {livePinned.troopNumber}{pinnedGroupName ? ' · ' + pinnedGroupName : ''}
                </div>
              </div>
              <div className="pinned-time-box">
                <div className="pinned-time-val">{fmt(livePinned.elapsedTimeInMilliseconds)}</div>
                <div className="pinned-time-lbl">Your Time</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 6. Category Tabs */}
      <div className="tabs-wrap">
        {TABS.map((t, i) => (
          <button
            key={t.key}
            className={'tab-btn ' + t.cls + (i === currentTab ? ' active' : '')}
            onClick={() => setCurrentTab(i)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="last-upd">{lastUpd}</div>

      {/* 7. Individual Rankings */}
      <div className="section-wrap">
        <div className="section-card">
          <div className="section-header">🏅 Individual Rankings</div>
          {indiv.length ? (
            indiv.slice(0, 10).map((e, idx) => (
              <div key={idx} className="lb-row" onClick={() => pinEntry(e)}>
                {rankBubble(e.groupRank)}
                <div className="row-info">
                  <div className="row-name">{e.firstName} {e.lastInitial}</div>
                  <div className="row-troop">Troop {e.troopNumber}</div>
                </div>
                <div className="row-time">{fmt(e.elapsedTimeInMilliseconds)}</div>
                <span className="pin-hint">📌</span>
              </div>
            ))
          ) : (
            <div className="empty"><div className="empty-icon">🌟</div>No scores yet — be the first!</div>
          )}
        </div>
      </div>

      {/* 8. Photo Gallery */}
      <div className="photo-section">
        <div className="photo-hdr">
          <span>📷 Conference Photo Board</span>
          <span className="photo-pending">{photos.length} photos</span>
        </div>
        <div className="photo-grid">
          {photos.slice(0, 12).length ? (
            photos.slice(0, 12).map(photo => (
              <div key={photo.id} className="photo-item" onClick={() => setSelectedPhoto(photo)} style={{ cursor: 'pointer' }}>
                <img src={photo.cloudinary_url} alt="Uploaded" className="photo-img" />
              </div>
            ))
          ) : (
            <>
              <div className="photo-ph"><span className="photo-ph-icon">🖼️</span><span>Awaiting photos</span></div>
              <div className="photo-ph"><span className="photo-ph-icon">🖼️</span><span>Awaiting photos</span></div>
              <div className="photo-ph"><span className="photo-ph-icon">🖼️</span><span>Awaiting photos</span></div>
              <div className="photo-ph"><span className="photo-ph-icon">🖼️</span><span>Awaiting photos</span></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
*{box-sizing:border-box;margin:0;padding:0}
:root{
  --eb:#1B5E9B;--eb-dark:#154d80;--ey:#F5B800;
  --gs:#00843D;--gs-mid:#00A84F;--gs-dark:#005C2A;
  --pink:#FF6B9D;--purple:#9B59B6;--teal:#00BCD4;
  --daisy:#3498DB;--brownie:#8B5E3C;--junior:#8E44AD;--senior:#E84393;
  --bg:#FFF5FB;--card:#FFFFFF;--border:#F0E6F0;
  --text-primary:#2D1B69;--text-secondary:#6B4E8A;--text-muted:#B39DCC;
}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:var(--bg);min-height:100vh}
.app{padding-bottom:2.5rem;position:relative;z-index:1}
body::before{content:'';position:fixed;top:0;left:0;right:0;height:100%;background-image:radial-gradient(circle,#FF6B9D22 2px,transparent 2px),radial-gradient(circle,#F5B80022 2px,transparent 2px),radial-gradient(circle,#00843D22 2px,transparent 2px);background-size:40px 40px,60px 60px,50px 50px;background-position:0 0,20px 20px,10px 30px;pointer-events:none;z-index:0}
.header{background:linear-gradient(135deg,var(--eb) 0%,#2471C8 100%);padding:14px 16px 16px;box-shadow:0 3px 12px rgba(27,94,155,0.35)}
.header-inner{display:flex;align-items:center;gap:12px}
.header-logo{width:130px;height:130px;object-fit:contain;background:transparent;flex-shrink:0}
.header-titles{color:white}
.header-main{font-size:18px;font-weight:800;line-height:1.2;letter-spacing:-0.3px}
.header-sub{font-size:11px;opacity:0.75;margin-top:3px;font-style:italic}
.evans-banner{background:white;border-bottom:0.5px solid #F0E6F0;padding:12px 16px}
.evans-text{font-size:13px;color:var(--text-secondary);line-height:1.6;margin:0}
.evans-link{color:var(--eb);font-weight:600;text-decoration:none}
.evans-link:hover{text-decoration:underline}
.gs-ribbon{background:linear-gradient(90deg,var(--gs-dark) 0%,var(--gs-mid) 100%);padding:8px 16px;display:flex;align-items:center;justify-content:space-between}
.live-pill{background:rgba(255,255,255,0.2);color:white;font-size:10px;font-weight:800;padding:5px 12px;border-radius:20px;letter-spacing:1px;display:flex;align-items:center;gap:6px;border:1px solid rgba(255,255,255,0.3)}
.live-dot{width:7px;height:7px;background:#7FE8A2;border-radius:50%;animation:pulse 1.4s infinite;box-shadow:0 0 6px #7FE8A2}
@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.4;transform:scale(0.8)}}
.refresh-text{color:rgba(255,255,255,0.75);font-size:11px}
.search-wrap{padding:14px 16px 0;position:relative}
.search-inner{display:flex;align-items:center;gap:10px;background:white;border:2.5px solid #F0D0F0;border-radius:28px;padding:11px 18px;box-shadow:0 4px 14px rgba(155,89,182,0.1);transition:border-color 0.2s,box-shadow 0.2s}
.search-inner:focus-within{border-color:var(--pink);box-shadow:0 4px 18px rgba(255,107,157,0.2)}
.search-icon{font-size:17px;flex-shrink:0}
.search-inner input{border:none;background:none;outline:none;font-size:14px;color:var(--text-primary);width:100%;font-weight:500}
.search-inner input::placeholder{color:var(--text-muted)}
.search-clear{background:rgba(155,89,182,0.12);border:none;color:var(--text-muted);width:22px;height:22px;border-radius:50%;font-size:11px;cursor:pointer;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.search-dropdown{position:absolute;left:16px;right:16px;top:100%;margin-top:6px;background:white;border:2px solid #F0D0F0;border-radius:16px;box-shadow:0 10px 30px rgba(155,89,182,0.22);overflow:hidden;z-index:50;max-height:340px;overflow-y:auto}
.search-result{display:flex;align-items:center;gap:11px;padding:11px 14px;border-bottom:1px solid #FAF5FF;cursor:pointer;transition:background 0.12s}
.search-result:last-child{border-bottom:none}
.search-result:hover{background:#FDF5FF}
.search-result:active{background:#F5E6FF}
.sr-rank{min-width:30px;height:26px;padding:0 8px;border-radius:13px;background:#F5F0FF;color:var(--junior);font-size:12px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.sr-info{flex:1;min-width:0}
.sr-name{font-size:14px;font-weight:700;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sr-meta{font-size:11px;color:var(--text-muted);margin-top:2px}
.sr-time{font-size:13px;font-weight:800;color:var(--text-primary);font-variant-numeric:tabular-nums;flex-shrink:0}
.sr-pin{font-size:15px;flex-shrink:0;opacity:0.4}
.search-empty{padding:18px 14px;text-align:center;font-size:13px;color:var(--text-muted)}
.pinned-section{margin:12px 16px 0}
.pinned-card{background:linear-gradient(135deg,#6B35A8 0%,#9B59B6 60%,#C8479B 100%);border-radius:18px;padding:14px 16px;position:relative;overflow:hidden;border:3px solid var(--ey);box-shadow:0 6px 20px rgba(107,53,168,0.35)}
.pinned-sparkle1{position:absolute;top:8px;right:40px;font-size:18px;opacity:0.5;animation:spin 4s linear infinite}
.pinned-sparkle2{position:absolute;bottom:8px;right:14px;font-size:14px;opacity:0.4;animation:spin 6s linear infinite reverse}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
.pinned-label-row{display:flex;align-items:center;gap:8px;margin-bottom:10px}
.pinned-badge{background:var(--ey);color:#7B4F00;font-size:10px;font-weight:900;padding:4px 12px;border-radius:20px;letter-spacing:0.5px}
.pinned-label-text{color:rgba(255,255,255,0.65);font-size:11px}
.pinned-content{display:flex;align-items:center;gap:12px}
.pinned-trophy{background:var(--ey);width:46px;height:46px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:22px;box-shadow:0 3px 10px rgba(0,0,0,0.2)}
.pinned-info{flex:1}
.pinned-name{color:white;font-size:15px;font-weight:800;line-height:1.2}
.pinned-troop{color:rgba(255,255,255,0.6);font-size:12px;margin-top:3px}
.pinned-time-box{background:rgba(0,0,0,0.2);border-radius:12px;padding:8px 14px;text-align:center;border:1.5px solid rgba(255,255,255,0.2)}
.pinned-time-val{color:var(--ey);font-size:18px;font-weight:900;font-variant-numeric:tabular-nums;letter-spacing:-0.5px}
.pinned-time-lbl{color:rgba(255,255,255,0.55);font-size:9px;margin-top:2px;letter-spacing:0.6px;text-transform:uppercase;font-weight:700}
.unpin-btn{position:absolute;top:10px;right:10px;background:rgba(255,255,255,0.15);border:none;color:rgba(255,255,255,0.8);width:26px;height:26px;border-radius:50%;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.15s}
.unpin-btn:hover{background:rgba(255,255,255,0.3)}
.no-pinned{border:2.5px dashed #E8D0F0;border-radius:18px;padding:18px 16px;text-align:center;background:white}
.no-pinned-icon{font-size:28px;margin-bottom:6px}
.no-pinned-text{font-size:14px;color:var(--text-primary);font-weight:700}
.no-pinned-hint{font-size:12px;color:var(--text-muted);margin-top:5px}
.tabs-wrap{padding:14px 16px 0;display:flex;gap:6px}
.tab-btn{flex:1;padding:8px 4px;border-radius:20px;font-size:10.5px;font-weight:700;border:2px solid;cursor:pointer;transition:all 0.2s;background:white;box-shadow:0 2px 6px rgba(0,0,0,0.06);text-align:center;line-height:1.15}
.tab-btn.active{box-shadow:0 3px 10px rgba(0,0,0,0.15)}
.tab-daisy{border-color:var(--daisy);color:var(--daisy)}
.tab-daisy.active{background:var(--daisy);color:white}
.tab-brownie{border-color:var(--brownie);color:var(--brownie)}
.tab-brownie.active{background:var(--brownie);color:white}
.tab-junior{border-color:var(--junior);color:var(--junior)}
.tab-junior.active{background:var(--junior);color:white}
.tab-senior{border-color:var(--senior);color:var(--senior)}
.tab-senior.active{background:var(--senior);color:white}
.section-wrap{padding:12px 16px 0}
.section-card{background:white;border-radius:18px;overflow:hidden;border:2px solid var(--border);box-shadow:0 4px 16px rgba(155,89,182,0.08)}
.section-header{padding:12px 16px;font-size:13px;font-weight:800;display:flex;align-items:center;gap:7px;border-bottom:2px solid #FAF0FF;color:var(--text-primary);background:linear-gradient(90deg,#FAF0FF,white)}
.lb-row{display:flex;align-items:center;padding:12px 16px;border-bottom:1.5px solid #FAF5FF;gap:11px;cursor:pointer;transition:background 0.12s;-webkit-tap-highlight-color:rgba(0,0,0,0.04)}
.lb-row:last-child{border-bottom:none}
.lb-row:hover{background:#FDF5FF}
.lb-row:active{background:#F5E6FF}
.rank-bubble{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:900;flex-shrink:0}
.r1{background:linear-gradient(135deg,#FFE566,#F5B800);color:#7B4F00;box-shadow:0 2px 8px rgba(245,184,0,0.4)}
.r2{background:linear-gradient(135deg,#F0F0F0,#C8C8C8);color:#555}
.r3{background:linear-gradient(135deg,#FDDBC7,#E8A87C);color:#7B3D1A}
.rn{background:#F5F0FF;color:#B39DCC;font-size:11px;font-weight:700}
.row-info{flex:1;min-width:0}
.row-name{font-size:14px;color:var(--text-primary);font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.row-troop{font-size:11px;color:var(--text-muted);margin-top:2px}
.row-time{font-size:13px;color:var(--text-primary);font-weight:800;font-variant-numeric:tabular-nums;flex-shrink:0}
.pin-hint{font-size:16px;flex-shrink:0;margin-left:4px;opacity:0.25;transition:opacity 0.15s}
.lb-row:hover .pin-hint{opacity:0.7}
.lb-row:active .pin-hint{opacity:1}
.last-upd{text-align:center;font-size:10px;color:var(--text-muted);padding:10px 16px 0}
.empty{padding:28px;text-align:center;font-size:13px;color:var(--text-muted)}
.empty-icon{font-size:32px;margin-bottom:8px}
.photo-section{margin:12px 16px 0;background:white;border-radius:18px;overflow:hidden;border:2px solid #C6F6D5;box-shadow:0 4px 14px rgba(0,132,61,0.08);position:relative;z-index:1}
.photo-hdr{background:linear-gradient(90deg,var(--gs-dark),var(--gs-mid));color:white;padding:12px 16px;font-size:13px;font-weight:800;display:flex;align-items:center;justify-content:space-between}
.photo-pending{background:rgba(0,0,0,0.2);font-size:10px;padding:3px 10px;border-radius:10px;font-weight:600}
.photo-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4px;padding:4px;max-height:600px;overflow-y:auto}
.photo-item{position:relative;aspect-ratio:1;overflow:hidden;border-radius:10px;background:#f0f0f0}
.photo-img{width:100%;height:100%;object-fit:cover;display:block}
.photo-ph{background:linear-gradient(135deg,#F0FFF4,#E6FFED);aspect-ratio:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;color:#68D391;font-size:12px;font-weight:600;border-radius:10px}
.photo-ph-icon{font-size:30px}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:9999}
.photo-modal{position:relative;max-width:90vw;max-height:90vh;background:white;border-radius:12px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.4)}
.photo-modal-img{width:100%;height:100%;object-fit:contain;max-width:90vw;max-height:90vh}
.modal-close{position:absolute;top:10px;right:10px;background:rgba(0,0,0,0.6);border:none;color:white;width:32px;height:32px;border-radius:50%;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.15s;z-index:10000}
.modal-close:hover{background:rgba(0,0,0,0.8)}
.winners-overlay{background:rgba(0,0,0,0.8)}
.winners-modal{background:white;border-radius:18px;padding:24px;max-width:500px;max-height:80vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,0.5)}
.winners-title{font-size:24px;font-weight:900;color:var(--text-primary);text-align:center;margin-bottom:20px;letter-spacing:1px}
.winners-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.winners-group{background:linear-gradient(135deg,#FAF0FF,#F5E6FF);border-radius:12px;padding:14px;border:2px solid #E8D0F0}
.winners-group-title{font-size:13px;font-weight:800;color:var(--text-primary);margin-bottom:10px;text-align:center;padding-bottom:8px;border-bottom:2px solid #E8D0F0}
.winner-row{display:flex;align-items:center;gap:8px;padding:8px 0;font-size:12px;color:var(--text-secondary)}
.winner-medal{font-size:18px;flex-shrink:0}
.winner-name{font-weight:700;flex:1;overflow:hidden;text-overflow:ellipsis}
.winner-time{font-weight:600;font-size:11px;color:var(--text-muted);font-variant-numeric:tabular-nums}
.no-winners{text-align:center;font-size:11px;color:var(--text-muted);padding:8px;font-style:italic}
.winners-toggle{position:fixed;bottom:20px;right:20px;background:linear-gradient(135deg,#FFE566,#F5B800);color:#7B4F00;border:none;width:56px;height:56px;border-radius:50%;font-size:28px;cursor:pointer;box-shadow:0 4px 14px rgba(245,184,0,0.4);transition:all 0.2s;z-index:100}
.winners-toggle:hover{transform:scale(1.1);box-shadow:0 6px 20px rgba(245,184,0,0.6)}
.winners-toggle:active{transform:scale(0.95)}
`;
